import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CardDetail,
  type ChecklistItem,
  type ContentBlock,
  useAddChecklistItem,
  useDeleteChecklistItem,
  useEditChecklistItem,
  useMoveChecklistItem,
  useSetBlockCollapsed,
  useToggleChecklistItem,
  useUpdateBlock,
} from '@/entities/card';
import { FormError } from '@/shared/forms';
import { stepMove } from '@/shared/lib/reorder';
import { Button } from '@/shared/ui';

import styles from './Content.module.css';

const ITEM_MAX = 1000;

export function ChecklistBlock({
  card,
  block,
  autoFocusTitle,
}: {
  card: CardDetail;
  block: ContentBlock;
  autoFocusTitle: boolean;
}) {
  const { t } = useTranslation();
  const checklist = block.checklist;
  const collapse = useSetBlockCollapsed(card.id);
  const rename = useUpdateBlock(card.id);
  const addItem = useAddChecklistItem(card.id);
  const toggle = useToggleChecklistItem(card.id);
  const moveItem = useMoveChecklistItem(card.id);
  const deleteItem = useDeleteChecklistItem(card.id);
  const [title, setTitle] = useState(checklist?.title ?? '');
  const [newItem, setNewItem] = useState('');
  // The input is hidden until asked for: a quiet "+ Add item" opens it.
  const [adding, setAdding] = useState(false);
  if (!checklist) return null;

  const ids = checklist.items.map((item) => item.id);
  const percent = checklist.total ? Math.round((checklist.completed / checklist.total) * 100) : 0;
  const titleId = `checklist-${block.id}`;
  const error =
    collapse.error ?? rename.error ?? toggle.error ?? moveItem.error ?? deleteItem.error;

  const saveTitle = () => {
    const next = title.trim();
    if (next !== (checklist.title ?? '')) rename.mutate({ blockId: block.id, title: next || null });
  };
  const submitItem = (event: SyntheticEvent) => {
    event.preventDefault();
    const text = newItem.trim();
    if (!text) return;
    addItem.mutate(
      { checklistId: checklist.id, text },
      {
        // Saved: reset and close. On error the text stays, the input stays open and the error
        // is shown right under it.
        onSuccess: () => {
          setNewItem('');
          setAdding(false);
        },
      },
    );
  };

  return (
    <div className={styles.checklist} aria-labelledby={titleId} role="group">
      <div className={styles.checklistHeader}>
        <button
          type="button"
          className={styles.tool}
          aria-expanded={!block.is_collapsed}
          aria-controls={`${titleId}-items`}
          aria-label={t(block.is_collapsed ? 'checklist.expand' : 'checklist.collapse')}
          onClick={() => {
            collapse.mutate({ blockId: block.id, collapsed: !block.is_collapsed });
          }}
        >
          {block.is_collapsed ? '▸' : '▾'}
        </button>
        <input
          id={titleId}
          className={styles.checklistTitle}
          value={title}
          maxLength={200}
          placeholder={t('checklist.untitled')}
          aria-label={t('checklist.titleLabel')}
          autoFocus={autoFocusTitle}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          onBlur={saveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
        <span className={styles.progressText}>
          {t('checklist.progress', {
            completed: checklist.completed,
            total: checklist.total,
            percent,
          })}
        </span>
      </div>
      <div
        className={styles.progressBar}
        role="progressbar"
        aria-label={t('checklist.progressLabel')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      <FormError error={error} />
      {!block.is_collapsed && (
        <div id={`${titleId}-items`}>
          {checklist.items.length === 0 && <p className={styles.empty}>{t('checklist.empty')}</p>}
          <ul className={styles.items}>
            {checklist.items.map((item, index) => (
              <ItemRow
                key={item.id}
                cardId={card.id}
                item={item}
                isFirst={index === 0}
                isLast={index === ids.length - 1}
                onToggle={() => {
                  toggle.mutate({ itemId: item.id, completed: !item.is_completed });
                }}
                onMove={(direction) => {
                  const step = stepMove(ids, item.id, direction, 'item');
                  if (step) moveItem.mutate({ itemId: item.id, ...step });
                }}
                onDelete={() => {
                  deleteItem.mutate(item.id);
                }}
              />
            ))}
          </ul>
          {adding ? (
            <>
              <form
                className={styles.addItem}
                onSubmit={submitItem}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.stopPropagation();
                    setNewItem('');
                    setAdding(false);
                  }
                }}
              >
                <input
                  className={styles.itemInput}
                  value={newItem}
                  maxLength={ITEM_MAX}
                  placeholder={t('checklist.addItem')}
                  aria-label={t('checklist.addItem')}
                  autoFocus
                  onChange={(e) => {
                    setNewItem(e.target.value);
                  }}
                />
                <Button type="submit" size="sm" disabled={addItem.isPending || !newItem.trim()}>
                  {t('checklist.add')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setNewItem('');
                    setAdding(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </form>
              <FormError error={addItem.error} />
            </>
          ) : (
            <button
              type="button"
              className={styles.addItemOpen}
              onClick={() => {
                setAdding(true);
              }}
            >
              + {t('checklist.addItemAction')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface ItemRowProps {
  cardId: string;
  item: ChecklistItem;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onDelete: () => void;
}

function ItemRow({ cardId, item, isFirst, isLast, onToggle, onMove, onDelete }: ItemRowProps) {
  const { t } = useTranslation();
  const edit = useEditChecklistItem(cardId);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.text);

  const save = () => {
    const text = value.trim();
    setEditing(false);
    if (text && text !== item.text) edit.mutate({ itemId: item.id, text });
    else setValue(item.text);
  };

  return (
    <li className={`${styles.item} ${item.is_completed ? styles.itemDone : ''}`}>
      <input
        type="checkbox"
        className={styles.itemCheck}
        checked={item.is_completed}
        aria-label={item.text}
        onChange={onToggle}
      />
      {editing ? (
        <input
          className={styles.itemInput}
          value={value}
          maxLength={ITEM_MAX}
          autoFocus
          aria-label={t('checklist.itemText')}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setValue(item.text);
              setEditing(false);
            }
          }}
        />
      ) : (
        <button
          type="button"
          className={styles.itemText}
          onClick={() => {
            setValue(item.text);
            setEditing(true);
          }}
        >
          {item.text}
        </button>
      )}
      <div className={styles.itemTools}>
        <button
          type="button"
          className={styles.tool}
          aria-label={t('checklist.moveUp', { text: item.text })}
          disabled={isFirst}
          onClick={() => {
            onMove('up');
          }}
        >
          ↑
        </button>
        <button
          type="button"
          className={styles.tool}
          aria-label={t('checklist.moveDown', { text: item.text })}
          disabled={isLast}
          onClick={() => {
            onMove('down');
          }}
        >
          ↓
        </button>
        <button
          type="button"
          className={`${styles.tool} ${styles.toolDanger}`}
          aria-label={t('checklist.deleteItem', { text: item.text })}
          onClick={onDelete}
        >
          ×
        </button>
      </div>
      <FormError error={edit.error} />
    </li>
  );
}
